import React, { useState, useEffect, useCallback, useMemo, useContext, useRef } from 'react';
import { KatRow, KatColumn, KatButton, KatTabs, KatTab } from '@amzn/katal-react';
import { makeAwsSignedRequest, getApiUrlForRealm } from '../services/ApiService';
import { formatUTC, parseGeneratedAt, prettyDisplayTransform, convertTimeWindowToDateRange, TIME_WINDOW_OPTIONS } from '../utils/commonUtils';
import { Environment, ENVIRONMENTS, REALM_TO_REGION } from '../constants';
import { useFilterState } from '../hooks/useFilterState';
import { AuthenticatedUserContext } from './AuthenticationController';
import SherlockHomepage from './SherlockHomepage';
import DiagnosticView from './DiagnosticView';
import MultiSelectDropdown from './MultiSelectDropdown';
import FilterHeader from './common/FilterHeader';
import { LoadingIndicator } from './common/LoadingStates';
import { IconTotal, IconPending, IconApproved, IconDeclined, IconWithinSla, IconOverSla } from './common/Icons';
import NumberFormatter from '../utils/NumberFormatter';
import AppConfigDeployer from './AppConfigDeployer';
import TranslatePage from './Translate';
import TrackerTrendPanel from './TrackerTrendPanel';
import BacklogBurndownPanel from './BacklogBurndownPanel';
import TrendRangeSlider from './TrendRangeSlider';
import PendingDeltaPanel from './PendingDeltaPanel';
import BatcherPage from './Batcher/BatcherPage';
import StuckWorkflow from './StuckWorkflow/StuckWorkflow';
import './SherlockDashboard.scss';

import '@amzn/harmony-types';

// Arrows
import { Xwrapper, useXarrow } from 'react-xarrows';
import Xarrow from 'react-xarrows';

// Router
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

/* ---------- Arrow wrapper ---------- */
interface ArrowComponentProps {
    id: string;
    from: string;
    to: string;
    color?: string;
    size?: number;
    startSocket?: 'left' | 'right' | 'top' | 'bottom';
    endSocket?: 'left' | 'right' | 'top' | 'bottom';
}

const ArrowComponent: React.FC<ArrowComponentProps> = ({
                                                           id,
                                                           from,
                                                           to,
                                                           color = '#000',
                                                           size = 3,
                                                           startSocket = 'right',
                                                           endSocket = 'left',
                                                       }) => {
    const updateXarrow = useXarrow();
    useEffect(() => {
        const t1 = setTimeout(updateXarrow, 0);
        const t2 = setTimeout(updateXarrow, 250);
        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
        };
    }, [updateXarrow]);

    return (
        <Xarrow
            key={id}
            start={from}
            end={to}
            startAnchor={startSocket as any}
            endAnchor={endSocket as any}
            color={color}
            strokeWidth={size}
            headSize={5}
            showHead
            zIndex={0}
            passProps={{ pointerEvents: 'none' }}
        />
    );
};

/* ---------- Types ---------- */
interface TrackerData {
    created: number;
    approved: number;
    declined: number;
    pending: number;
    batched: number;
    unbatched: number;
    unstarted: number;
    withinSla: number;
    overSlaCount: number;
    totalRefundsCreated: number;
    refundsPending: number;
    overSla: number;
    realm: string;
    timeWindow: string;
    clientId?: string;
    metadata?: {
        activeClientIds?: string[];
        generatedAt?: string;
        [key: string]: any;
    };
}

interface DashboardOption {
    value: string;
    label: string;
    enabled: boolean;
}

const TRACKER_ALL_CLIENTS = 'AllClients';
const DEFAULT_CLIENT_OPTIONS = ['AWS-Payments', 'AWS-Payments-KR', 'Mendel', 'AmazonRetail', 'Audible'];

const toFiniteNumber = (value: unknown): number | null => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    if (typeof value === 'string' && value.trim() !== '') {
        const parsedValue = Number(value);
        return Number.isFinite(parsedValue) ? parsedValue : null;
    }

    return null;
};

const toOptionalString = (value: unknown): string | undefined =>
    typeof value === 'string' && value.trim() !== '' ? value : undefined;

const toMetadata = (value: unknown, derivedActiveClientIds?: string[]): TrackerData['metadata'] | undefined => {
    if (!value || typeof value !== 'object') {
        return derivedActiveClientIds && derivedActiveClientIds.length > 0
            ? { activeClientIds: derivedActiveClientIds }
            : undefined;
    }

    const metadata: NonNullable<TrackerData['metadata']> = {
        ...(value as Record<string, unknown>),
    };
    const activeClientIds = Array.isArray(metadata.activeClientIds)
        ? metadata.activeClientIds.filter((clientId): clientId is string => typeof clientId === 'string' && clientId.trim() !== '')
        : undefined;

    if (activeClientIds && activeClientIds.length > 0) {
        metadata.activeClientIds = activeClientIds;
        return metadata;
    }

    if (derivedActiveClientIds && derivedActiveClientIds.length > 0) {
        metadata.activeClientIds = derivedActiveClientIds;
    }

    return metadata;
};

const deriveTrackerDataFromCounts = (
    countsSource: Record<string, unknown>,
    realm: string,
    timeWindow: string,
    clientId?: string,
    metadata?: TrackerData['metadata']
): TrackerData | null => {
    const created = toFiniteNumber(countsSource.created) ?? 0;
    const approved = toFiniteNumber(countsSource.approved) ?? 0;
    const declined = toFiniteNumber(countsSource.declined) ?? 0;
    const batched = toFiniteNumber(countsSource.batched) ?? 0;
    const unbatched = toFiniteNumber(countsSource.unbatched) ?? 0;
    const unstarted = toFiniteNumber(countsSource.unstarted) ?? 0;
    const withinSla = toFiniteNumber(countsSource.withinSla) ?? 0;
    const overSla = toFiniteNumber(countsSource.overSla)
        ?? toFiniteNumber(countsSource.overSlaCount)
        ?? (batched + unbatched + unstarted);
    const refundsPending = toFiniteNumber(countsSource.refundsPending)
        ?? toFiniteNumber(countsSource.pending)
        ?? (withinSla + overSla);
    const totalRefundsCreated = toFiniteNumber(countsSource.totalRefundsCreated)
        ?? (created + refundsPending + approved + declined);

    const hasRecognizedCountField = [
        'created',
        'approved',
        'declined',
        'pending',
        'batched',
        'unbatched',
        'unstarted',
        'withinSla',
        'refundsPending',
        'totalRefundsCreated',
        'overSla',
        'overSlaCount',
    ].some((fieldName) => countsSource[fieldName] !== undefined && countsSource[fieldName] !== null);

    if (!hasRecognizedCountField && !metadata?.generatedAt) {
        return null;
    }

    return {
        created,
        approved,
        declined,
        pending: refundsPending,
        batched,
        unbatched,
        unstarted,
        withinSla,
        overSlaCount: overSla,
        totalRefundsCreated,
        refundsPending,
        overSla,
        realm,
        timeWindow,
        clientId,
        metadata,
    };
};

const aggregateRawTrackerFile = (
    clientFilters: Record<string, any>,
    selectedClients: string[],
    timeWindow: string
): Record<string, number> | null => {
    const resolvedClients = selectedClients.length === 0 || selectedClients.includes(TRACKER_ALL_CLIENTS)
        ? [TRACKER_ALL_CLIENTS]
        : selectedClients;
    const aggregatedCounts = {
        created: 0,
        approved: 0,
        declined: 0,
        pending: 0,
        batched: 0,
        unbatched: 0,
        unstarted: 0,
        withinSla: 0,
    };
    let matchedClient = false;

    for (const clientId of resolvedClients) {
        const timeWindowCounts = clientFilters?.[clientId]?.timeWindows?.[timeWindow];
        if (!timeWindowCounts || typeof timeWindowCounts !== 'object') {
            continue;
        }

        matchedClient = true;
        aggregatedCounts.created += toFiniteNumber(timeWindowCounts.created) ?? 0;
        aggregatedCounts.approved += toFiniteNumber(timeWindowCounts.approved) ?? 0;
        aggregatedCounts.declined += toFiniteNumber(timeWindowCounts.declined) ?? 0;
        aggregatedCounts.pending += toFiniteNumber(timeWindowCounts.pending) ?? 0;
        aggregatedCounts.batched += toFiniteNumber(timeWindowCounts.batched) ?? 0;
        aggregatedCounts.unbatched += toFiniteNumber(timeWindowCounts.unbatched) ?? 0;
        aggregatedCounts.unstarted += toFiniteNumber(timeWindowCounts.unstarted) ?? 0;
        aggregatedCounts.withinSla += toFiniteNumber(timeWindowCounts.withinSla) ?? 0;
    }

    return matchedClient ? aggregatedCounts : null;
};

const unwrapTrackerPayload = (value: unknown): unknown => {
    let currentValue = value;

    for (let depth = 0; depth < 4; depth += 1) {
        if (!currentValue || typeof currentValue !== 'object') {
            return currentValue;
        }

        const wrappedValue = (currentValue as Record<string, unknown>).data
            ?? (currentValue as Record<string, unknown>).trackerData
            ?? (currentValue as Record<string, unknown>).payload
            ?? (currentValue as Record<string, unknown>).result;

        if (wrappedValue === undefined || wrappedValue === currentValue) {
            return currentValue;
        }

        currentValue = wrappedValue;
    }

    return currentValue;
};

const normalizeTrackerDataResponse = (
    value: unknown,
    realm: string,
    timeWindow: string,
    selectedClients: string[]
): TrackerData | null => {
    const payload = unwrapTrackerPayload(value);
    if (!payload || typeof payload !== 'object') {
        return null;
    }

    const record = payload as Record<string, unknown>;
    const rawClientFilters = record.clientFilters;
    const derivedActiveClientIds = rawClientFilters && typeof rawClientFilters === 'object'
        ? Object.keys(rawClientFilters as Record<string, unknown>).filter((clientId) => clientId !== TRACKER_ALL_CLIENTS)
        : undefined;
    const metadata = toMetadata(record.metadata, derivedActiveClientIds);
    const clientId = toOptionalString(record.clientId)
        ?? (selectedClients.length === 0 || selectedClients.includes(TRACKER_ALL_CLIENTS)
            ? TRACKER_ALL_CLIENTS
            : selectedClients.join(','));
    const normalizedTimeWindow = toOptionalString(record.timeWindow) ?? timeWindow;

    const directCounts = deriveTrackerDataFromCounts(record, realm, normalizedTimeWindow, clientId, metadata);
    if (directCounts) {
        return directCounts;
    }

    if (rawClientFilters && typeof rawClientFilters === 'object') {
        const aggregatedCounts = aggregateRawTrackerFile(
            rawClientFilters as Record<string, any>,
            selectedClients,
            normalizedTimeWindow,
        );

        if (aggregatedCounts) {
            return deriveTrackerDataFromCounts(aggregatedCounts, realm, normalizedTimeWindow, clientId, metadata);
        }
    }

    return null;
};

/* =================================================================== */

const SherlockDashboard: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    
    // Get authentication context - single source like Tracer
    const { userAlias } = useContext(AuthenticatedUserContext);

    // Route-driven dashboard
    const onTrackerRoute = location.pathname === '/tracker';
    const onAppConfigRoute = location.pathname === '/appconfig';
    const onTranslateRoute = location.pathname === '/translate';
    const onBatcherRoute = location.pathname === '/batcher';
    const onStuckRoute = location.pathname === '/stuck';
    const selectedDashboard = onTrackerRoute
        ? 'tracker'
        : onAppConfigRoute
            ? 'appconfig'
            : onTranslateRoute
                ? 'translate'
                : onBatcherRoute
                    ? 'batcher'
                    : onStuckRoute
                        ? 'stuck'
                        : '';

    // App state
    const [trackerData, setTrackerData] = useState<TrackerData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Authentication
    const [username, setUsername] = useState<string | undefined>(undefined);
    const [authLoading, setAuthLoading] = useState(true);
    const [authError, setAuthError] = useState<string | null>(null);

    // Tabs
    const [selectedTrackerTab, setSelectedTrackerTab] = useState<string>('tracker');

    const environments = ENVIRONMENTS;

    // Initialize environment from URL or default based on hostname
    const [selectedEnvironment, setSelectedEnvironment] = useState<Environment>(() => {
        const hostname = window?.location?.hostname || '';

        // Parse region and stage from hostname pattern: {region}.{stage}.sherlock.payments.amazon.dev
        const match = hostname.match(/^([^.]+)\.([^.]+)\.sherlock\.payments\.amazon\.dev$/);

        if (match) {
            const [, region, stage] = match;
            // Map AWS regions to realms
            const regionToRealm: { [key: string]: string } = {
                'us-east-1': 'NAAmazon',
                'eu-south-2': 'eu-south-2',
                'us-west-2': 'FEAmazon'
            };

            const realm = regionToRealm[region] || 'NAAmazon';
            const env = environments.find(env => env.stage === stage && env.realm === realm);
            if (env) {
                return env;
            }
        }

        // Default fallback to Beta NA
        return environments.find(env => env.stage === 'beta' && env.realm === 'NAAmazon')!;
    });

    // This breaks the circular dependency where effects both read and write selectedEnvironment
    const selectedEnvironmentRef = useRef(selectedEnvironment);
    useEffect(() => {
        selectedEnvironmentRef.current = selectedEnvironment;
    }, [selectedEnvironment]);


    const dashboardOptions: DashboardOption[] = [
        { value: '', label: 'Select Dashboard...', enabled: true },
        { value: 'tracker', label: 'Refund Tracker Dashboard', enabled: true },
        // The AppConfigDeployer page handles its own access gate (team-membership check)
        // and renders an "Access denied" state for users not in paystation-refunds-team.
        { value: 'appconfig', label: 'AppConfig Deployer', enabled: true },
        { value: 'translate', label: 'Bulk Translate', enabled: true },
        { value: 'batcher', label: 'Batcher', enabled: true },
        { value: 'stuck', label: 'Stuck Workflows', enabled: true },
    ];

    // Ref to track if a redirect is in progress to prevent state updates
    const isRedirectingRef = useRef(false);

    // Ensure environment is correctly set based on hostname after component mounts
    useEffect(() => {
        // Skip if redirect is in progress
        if (isRedirectingRef.current) return;

        const hostname = window?.location?.hostname || '';
        const match = hostname.match(/^([^.]+)\.([^.]+)\.sherlock\.payments\.amazon\.dev$/);

        if (match) {
            const [, region, stage] = match;
            const regionToRealm: { [key: string]: string } = {
                'us-east-1': 'NAAmazon',
                'eu-south-2': 'eu-south-2',
                'us-west-2': 'FEAmazon'
            };

            const realm = regionToRealm[region] || 'NAAmazon';
            const env = environments.find(env => env.stage === stage && env.realm === realm);

            // Use ref for comparison to avoid triggering this effect again
            if (env && (env.stage !== selectedEnvironmentRef.current.stage || env.realm !== selectedEnvironmentRef.current.realm)) {
                setSelectedEnvironment(env);
            }
        }
    }, [environments]);

    // Use filter hook
    const { filters, filterOptions, updateFilter, updateFilterOptions } = useFilterState({
        timeWindow: searchParams.get('timeWindow') || 'now-1y',
        selectedClients: (() => {
            const urlClientId = searchParams.get('clientId');
            if (urlClientId === null) return ['AllClients'];
            if (urlClientId === '') return [];
            return urlClientId === 'AllClients' ? ['AllClients'] : urlClientId.split(',').map((id) => decodeURIComponent(id.trim()));
        })()
    });

    // Progressive disclosure
    const [showPendingAnalysis, setShowPendingAnalysis] = useState(false);
    const [showOverSlaAnalysis, setShowOverSlaAnalysis] = useState(false);

    // Custom date range for the trend/movement panels. Seeded from the coarse timeWindow but
    // independently adjustable (granular history supports a finer range than the timeWindow enum).
    const [trendStartDateTime, setTrendStartDateTime] = useState<Date | undefined>(undefined);
    const [trendEndDateTime, setTrendEndDateTime] = useState<Date | undefined>(undefined);

    // Keep the trend date range defaulted to the currently-selected timeWindow until the user
    // adjusts it. convertTimeWindowToDateRange mirrors the backend window resolution.
    useEffect(() => {
        const generatedAt = trackerData?.metadata?.generatedAt;
        const { startDate, endDate } = convertTimeWindowToDateRange(
            filters.timeWindow,
            generatedAt ? parseGeneratedAt(generatedAt) || undefined : undefined);
        setTrendStartDateTime(startDate);
        setTrendEndDateTime(endDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.timeWindow, trackerData?.metadata?.generatedAt]);

    // Full bounds of the selected timeWindow -- the slider's min/max. The selected sub-range
    // (trendStartDateTime/trendEndDateTime) lives within this.
    const trendWindow = useMemo(() => {
        const generatedAt = trackerData?.metadata?.generatedAt;
        return convertTimeWindowToDateRange(
            filters.timeWindow,
            generatedAt ? parseGeneratedAt(generatedAt) || undefined : undefined);
    }, [filters.timeWindow, trackerData?.metadata?.generatedAt]);

    // Diagnostic trigger
    const [triggerDiagnosticFetch, setTriggerDiagnosticFetch] = useState(false);

    const parsedGeneratedAt = useMemo(
        () => (trackerData?.metadata?.generatedAt ? parseGeneratedAt(trackerData.metadata.generatedAt) : null),
        [trackerData?.metadata?.generatedAt]
    );

    const clientOptions = useMemo(
        () => ['AllClients', ...filterOptions.availableClients.filter((c) => c !== 'AllClients').sort((a, b) => a.localeCompare(b))],
        [filterOptions.availableClients]
    );

    // Router helpers
    const ensureTrackerDefaults = useCallback(() => {
        // Skip if redirect is in progress or not on tracker route
        if (isRedirectingRef.current || !onTrackerRoute) return;
        
        const hasTW = searchParams.has('timeWindow');
        const hasClientParam = searchParams.has('clientId');
        const hasStage = searchParams.has('stage');
        const hasRegion = searchParams.has('region');
        
        // Early return if all params are present 
        if (hasTW && hasClientParam && hasStage && hasRegion) {
            return;
        }
        
        const params = new URLSearchParams(searchParams);
        let needsUpdate = false;
        
        // Use ref to get current environment values without adding to dependencies
        if (!hasTW) { params.set('timeWindow', 'now-1y'); needsUpdate = true; }
        if (!hasClientParam) { params.set('clientId', 'AllClients'); needsUpdate = true; }
        if (!hasStage) { params.set('stage', selectedEnvironmentRef.current.stage); needsUpdate = true; }
        if (!hasRegion) { params.set('region', selectedEnvironmentRef.current.realm); needsUpdate = true; }
        
        if (needsUpdate) {
            setSearchParams(params, { replace: true });
        }
    }, [onTrackerRoute, searchParams, setSearchParams]);

    const navigateToHomepage = useCallback(() => navigate('/home', { replace: false }), [navigate]);

    const navigateToTrackerDashboard = useCallback(() => {
        const params = new URLSearchParams();
        // Ensure we always have a stage - use current selectedEnvironment or default to beta
        const currentStage = selectedEnvironment?.stage || 'beta';
        const currentRegion = selectedEnvironment?.realm || 'NAAmazon';
        params.set('stage', currentStage);
        params.set('region', currentRegion);
        params.set('timeWindow', filters.timeWindow || 'now-1y');
        const clientIdForUrl =
            filters.selectedClients.includes('AllClients') || filters.selectedClients.length === 0 ? 'AllClients' : filters.selectedClients.join(',');
        params.set('clientId', clientIdForUrl);
        navigate({ pathname: '/tracker', search: `?${params.toString()}` }, { replace: false });

        // Kick off data fetch + diagnostic options fetch
        fetchTrackerData(selectedEnvironment.stage, selectedEnvironment.realm);
        setTriggerDiagnosticFetch((prev) => !prev);
    }, [navigate, filters.timeWindow, filters.selectedClients, selectedEnvironment]);

    const navigateToBatcher = useCallback(() => navigate('/batcher', { replace: false }), [navigate]);

    const navigateToStuckWorkflow = useCallback(() => navigate('/stuck', { replace: false }), [navigate]);

    const updateURL = useCallback(
        (newTimeWindow: string, newSelectedClients: string[]) => {
            const params = new URLSearchParams();
            params.set('stage', selectedEnvironment.stage);
            params.set('region', selectedEnvironment.realm);
            params.set('timeWindow', newTimeWindow);
            if (newSelectedClients.includes('AllClients')) {
                params.set('clientId', 'AllClients');
            } else if (newSelectedClients.length === 0) {
                // keep param present but empty to represent "no clients selected"
                params.set('clientId', '');
            } else {
                params.set('clientId', newSelectedClients.join(','));
            }

            if (!onTrackerRoute) {
                navigate({ pathname: '/tracker', search: `?${params.toString()}` }, { replace: false });
            } else {
                setSearchParams(params, { replace: false });
            }
        },
        [onTrackerRoute, navigate, setSearchParams, selectedEnvironment.stage, selectedEnvironment.realm]
    );

    // Sync local filter state with URL when /tracker search params change
    useEffect(() => {
        // Skip if redirect is in progress or not on tracker route
        if (isRedirectingRef.current || !onTrackerRoute) return;
        
        const tw = searchParams.get('timeWindow') || 'now-1y';
        const hasClientParam = searchParams.has('clientId');
        const cid = hasClientParam ? searchParams.get('clientId') : null;

        updateFilter('timeWindow', tw);
        if (cid === null) {
            updateFilter('selectedClients', ['AllClients']);
        } else if (cid === '') {
            updateFilter('selectedClients', []);
        } else {
            updateFilter('selectedClients', cid === 'AllClients' ? ['AllClients'] : cid.split(',').map((id) => decodeURIComponent(id.trim())));
        }

        // Sync environment from URL parameters
        const urlStage = searchParams.get('stage');
        const urlRegion = searchParams.get('region');
        if (urlStage && urlRegion) {
            const envFromUrl = environments.find(env => env.stage === urlStage && env.realm === urlRegion && env.enabled);
            // Use ref for comparison to prevent infinite loop
            if (envFromUrl && 
                (envFromUrl.stage !== selectedEnvironmentRef.current.stage || 
                 envFromUrl.realm !== selectedEnvironmentRef.current.realm)) {
                setSelectedEnvironment(envFromUrl);
            }
        }
    }, [onTrackerRoute, searchParams, updateFilter, environments]);

    // Set defaults if missing
    useEffect(() => {
        ensureTrackerDefaults();
    }, [ensureTrackerDefaults]);

    // Environment change
    const handleEnvironmentChange = (env: Environment) => {
        const targetRegion = REALM_TO_REGION[env.realm] || 'us-east-1';
        const currentHostname = window.location.hostname;
        const targetHostname = `${targetRegion}.${env.stage}.sherlock.payments.amazon.dev`;

        // If changing to different region/stage, redirect to appropriate domain.
        // Skip this on localhost (local dev) so we stay on the local server instead
        // of navigating to the real environment host.
        if (currentHostname !== targetHostname && currentHostname !== 'localhost') {
            // Set redirect flag to prevent any state updates during redirect
            isRedirectingRef.current = true;

            // Build URL with new environment params (preserve current filters)
            const params = new URLSearchParams();
            params.set('stage', env.stage);
            params.set('region', env.realm);
            params.set('timeWindow', searchParams.get('timeWindow') || 'now-1y');
            params.set('clientId', searchParams.get('clientId') || 'AllClients');

            const targetUrl = `https://${targetHostname}${window.location.pathname}?${params.toString()}`;

            // Do NOT update any React state - just redirect
            window.location.href = targetUrl;
            return;
        }

        // Same domain - just update state and URL params
        setSelectedEnvironment(env);
        setShowPendingAnalysis(false);
        setShowOverSlaAnalysis(false);

        if (onTrackerRoute) {
            const params = new URLSearchParams(searchParams);
            params.set('stage', env.stage);
            params.set('region', env.realm);
            setSearchParams(params, { replace: true });
            fetchTrackerData(env.stage, env.realm);
            setTriggerDiagnosticFetch((prev) => !prev);
        }
    };

    // Fetch tracker data
    const fetchTrackerData = async (stage = selectedEnvironment.stage, realm = selectedEnvironment.realm) => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ timeWindow: filters.timeWindow });
            const selectedClients = filters.selectedClients;

            if (selectedClients.includes(TRACKER_ALL_CLIENTS)) {
                params.set('clientId', TRACKER_ALL_CLIENTS);
            } else if (selectedClients.length > 0) {
                params.set('clientId', selectedClients.join(','));
            }

            const endpoint = `tracker?${params.toString()}`;
            const data = await makeAwsSignedRequest(stage, realm, endpoint);
            const normalizedData = normalizeTrackerDataResponse(data, realm, filters.timeWindow, selectedClients);

            if (!normalizedData) {
                throw new Error('Tracker API returned an unexpected payload shape');
            }

            setTrackerData(normalizedData);

            if (normalizedData.metadata?.activeClientIds?.length) {
                updateFilterOptions({ availableClients: normalizedData.metadata.activeClientIds });
            } else {
                updateFilterOptions({ availableClients: DEFAULT_CLIENT_OPTIONS });
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch tracker data');
            setTrackerData(null);
            updateFilterOptions({ availableClients: DEFAULT_CLIENT_OPTIONS });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (onTrackerRoute) {
            fetchTrackerData(selectedEnvironment.stage, selectedEnvironment.realm);
            setTriggerDiagnosticFetch((prev) => !prev);
        } else {
            setShowPendingAnalysis(false);
            setShowOverSlaAnalysis(false);
            setSelectedTrackerTab('tracker');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onTrackerRoute]);

    // Auth
    useEffect(() => {
        if (userAlias && userAlias !== 'Guest') {
            setUsername(userAlias);
            setAuthLoading(false);
        }
    }, [userAlias]);

    // Tab switching & arrow refresh
    const [disableTransitions, setDisableTransitions] = useState(false);
    const updateXarrow = useXarrow();

    const handleTabSwitch = (newTab: string) => {
        if (newTab !== selectedTrackerTab) {
            setDisableTransitions(true);
            setSelectedTrackerTab(newTab);
            setTimeout(() => {
                updateXarrow();
                setDisableTransitions(false);
            }, 50);
        }
    };

    // Keep arrows aligned on resize
    useEffect(() => {
        let rafId: number | null = null;
        const onResize = () => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => updateXarrow());
        };
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, [updateXarrow]);

    // Refresh arrows when content toggles
    useEffect(() => {
        const t1 = setTimeout(updateXarrow, 0);
        const t2 = setTimeout(updateXarrow, 300);
        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
        };
    }, [showPendingAnalysis, showOverSlaAnalysis, selectedTrackerTab, loading, trackerData, updateXarrow]);

    // Helpers
    const [diagnosticFiltersFromTracker, setDiagnosticFiltersFromTracker] = useState<{
        startDateTime: Date;
        endDateTime: Date;
        selectedClients: string[];
        autoExecute: boolean;
        timestamp: number;
    } | null>(null);
    const [isDiagnosticSyncing, setIsDiagnosticSyncing] = useState(false);

    const handleGetDiagnostics = () => {
        if (isDiagnosticSyncing) return;
        setIsDiagnosticSyncing(true);

        const generatedAt = trackerData?.metadata?.generatedAt;
        const { startDate, endDate } = convertTimeWindowToDateRange(filters.timeWindow, generatedAt ? parseGeneratedAt(generatedAt) || undefined : undefined);
        const diagnosticClients = filters.selectedClients.includes('AllClients') ? ['AllClients'] : [...filters.selectedClients];

        setDiagnosticFiltersFromTracker({
            startDateTime: startDate,
            endDateTime: endDate,
            selectedClients: diagnosticClients,
            autoExecute: true,
            timestamp: Date.now(),
        });

        handleTabSwitch('diagnostic');
    };

    const selectedTimeWindowLabel = useMemo(
        () => TIME_WINDOW_OPTIONS.find((option) => option.value === filters.timeWindow)?.label ?? filters.timeWindow,
        [filters.timeWindow],
    );

    const trackerScopeLabel = useMemo(() => {
        if (!filters.selectedClients.length || filters.selectedClients.includes(TRACKER_ALL_CLIENTS)) {
            return 'All clients';
        }

        if (filters.selectedClients.length === 1) {
            return prettyDisplayTransform(filters.selectedClients[0]);
        }

        return `${filters.selectedClients.length} selected clients`;
    }, [filters.selectedClients]);

    // Early returns for auth
    if (authLoading) {
        return (
            <div className="sherlock-dashboard">
                <div className="auth-loading">
                    <div className="auth-loading-content">
                        <h2>Sherlock Dashboard</h2>
                        <p>Authenticating with Midway...</p>
                        <div className="loading-spinner"></div>
                    </div>
                </div>
            </div>
        );
    }

    if (authError) {
        return (
            <div className="sherlock-dashboard">
                <div className="auth-error">
                    <div className="auth-error-content">
                        <h2>Authentication Error</h2>
                        <p>{authError}</p>
                        <KatButton variant="primary" label="Retry Authentication" onClick={() => window.location.reload()} />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`sherlock-dashboard ${disableTransitions ? 'no-transitions' : ''}`}>
            {/* Nav */}
            <div className="navigation-bar">
                <div className="nav-logo">
                    <h1>Sherlock</h1>
                </div>
                <div className="nav-controls">
                    <div className="dashboard-selector">
                        <select
                            value={selectedDashboard}
                            onChange={(e) => {
                                setShowPendingAnalysis(false);
                                setShowOverSlaAnalysis(false);
                                if (e.target.value === '') navigateToHomepage();
                                if (e.target.value === 'tracker') navigateToTrackerDashboard();
                                if (e.target.value === 'appconfig') navigate('/appconfig');
                                if (e.target.value === 'translate') navigate('/translate');
                                if (e.target.value === 'batcher') navigateToBatcher();
                                if (e.target.value === 'stuck') navigateToStuckWorkflow();
                            }}
                            className="dashboard-select"
                        >
                            {dashboardOptions.map((option) => (
                                <option key={option.value} value={option.value} disabled={!option.enabled}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="environment-selector">
                        <select
                            value={environments.findIndex(
                                (env) => env.stage === selectedEnvironment.stage && env.realm === selectedEnvironment.realm
                            )}
                            onChange={(e) => handleEnvironmentChange(environments[parseInt(e.target.value, 10)])}
                            className="environment-select"
                            disabled={!onTrackerRoute && !onAppConfigRoute && !onTranslateRoute && !onBatcherRoute && !onStuckRoute}
                        >
                            {environments.map((env, index) => (
                                <option key={`${env.stage}-${env.realm}`} value={index} disabled={!env.enabled}>
                                    {env.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="username-display">
                        <span className="username">{username}</span>
                    </div>
                </div>
            </div>

            {/* Routes rendered inside this component */}
            {!onTrackerRoute && !onAppConfigRoute && !onTranslateRoute && !onBatcherRoute && !onStuckRoute && (
                <SherlockHomepage
                    onTrackerDashboardClick={navigateToTrackerDashboard}
                    onBatcherDashboardClick={navigateToBatcher}
                    onStuckWorkflowClick={navigateToStuckWorkflow}
                />
            )}

            {onAppConfigRoute && (
                <AppConfigDeployer
                    stage={selectedEnvironment.stage}
                    region={selectedEnvironment.realm}
                />
            )}

            {onTranslateRoute && (
                <TranslatePage
                    stage={selectedEnvironment.stage}
                    realm={selectedEnvironment.realm}
                />
            )}

            {onBatcherRoute && (
                <BatcherPage
                    stage={selectedEnvironment.stage}
                    realm={selectedEnvironment.realm}
                />
            )}

            {onStuckRoute && (
                <StuckWorkflow
                    stage={selectedEnvironment.stage}
                    region={selectedEnvironment.realm}
                />
            )}

            {onTrackerRoute && (
                <>
                    <KatRow noGutters>
                        <KatColumn xs={12}>
                            <div className="dashboard-header">
                                <div className="dashboard-header__copy">
                                    <h1 className="dashboard-title">Refund Tracker Dashboard</h1>
                                </div>
                            </div>
                        </KatColumn>
                    </KatRow>

                    <div className="tracker-tabs-section">
                        <KatTabs selected={selectedTrackerTab}>
                            <KatTab tabId="tracker" label="Tracker View" onClick={() => handleTabSwitch('tracker')}>
                                <div className="tab-content">
                                    {/* Filters */}
                                    <div className="filters-section">
                                        <FilterHeader 
                                            lastRefreshed={parsedGeneratedAt ? `${formatUTC(parsedGeneratedAt)} (UTC)` : 'Not yet loaded'}
                                            error={error}
                                        />

                                        <KatRow noGutters className="filters-row">
                                            <KatColumn md={3} sm={6} xs={12}>
                                                <div className="filter-item">
                                                    <label>Time Window</label>
                                                    <select
                                                        className="filter-select"
                                                        value={filters.timeWindow}
                                                        onChange={(e) => {
                                                            updateFilter('timeWindow', e.target.value);
                                                            updateURL(e.target.value, filters.selectedClients);
                                                        }}
                                                    >
                                                        {TIME_WINDOW_OPTIONS.map((option) => (
                                                            <option key={option.value} value={option.value}>
                                                                {option.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </KatColumn>

                                            <KatColumn md={3} sm={6} xs={12}>
                                                <div className="filter-item">
                                                    <MultiSelectDropdown
                                                        label="Client IDs"
                                                        placeholder="Select client IDs..."
                                                        options={clientOptions}
                                                        selectedOptions={filters.selectedClients}
                                                        onSelectionChange={(newSel) => {
                                                            updateFilter('selectedClients', newSel);
                                                            updateURL(filters.timeWindow, newSel);
                                                        }}
                                                        loading={loading}
                                                        allOption="AllClients"
                                                        enforceExclusiveAll
                                                        displayTransform={prettyDisplayTransform}
                                                    />
                                                </div>
                                            </KatColumn>

                                            <KatColumn md={3} sm={6} xs={12}>
                                                <div className="filter-actions">
                                                    <KatButton
                                                        variant="primary"
                                                        label={loading ? 'Loading...' : 'Search'}
                                                        onClick={() => {
                                                            if (onTrackerRoute) {
                                                                setShowPendingAnalysis(false);
                                                                setShowOverSlaAnalysis(false);
                                                                fetchTrackerData(selectedEnvironment.stage, selectedEnvironment.realm);
                                                                setTriggerDiagnosticFetch((prev) => !prev);
                                                            }
                                                        }}
                                                        disabled={loading}
                                                    />
                                                </div>
                                            </KatColumn>
                                        </KatRow>
                                    </div>

                                    {/* Flow */}
                                    <div className="flow-section">
                                        <div className="flow-section-header">
                                            <div>
                                                <p className="eyebrow">Live Snapshot</p>
                                                <h2>Current Refund Picture</h2>
                                                <p>
                                                    Follow the current refund state from total created into pending, then drill into over-SLA
                                                    backlog and diagnostics.
                                                </p>
                                            </div>
                                            <div className="flow-section-meta">
                                                <span>{selectedTimeWindowLabel}</span>
                                                <span>{trackerScopeLabel}</span>
                                            </div>
                                        </div>
                                        {loading && <LoadingIndicator message="Loading tracker data..." />}

                                        {!loading && (
                                            <Xwrapper>
                                                <div className="node-graph-container">
                                                    <div className="level-1-flow">
                                                        <div id="total-created-card" className="flow-card total-created-card">
                                                            <div className="card-header">
                                                                <h3>
                                  <span className="title-left">
                                    <IconTotal className="card-icon" />
                                    Total Refunds Created
                                  </span>
                                                                    <span
                                                                        className="info-icon"
                                                                        title="Total number of refunds in the system (Created + Pending + Approved + Declined)"
                                                                    >
                                    ℹ️
                                  </span>
                                                                </h3>
                                                            </div>
                                                            <div className="flow-card-content">
                                                                <div className="count-center">
                                                                    <div className="refund-count"><NumberFormatter number={trackerData?.totalRefundsCreated || 0} /></div>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div id="pending-card" className="flow-card pending-card">
                                                            <div className="card-header">
                                                                <h3>
                                  <span className="title-left">
                                    <IconPending className="card-icon" />
                                    Refunds Pending
                                  </span>
                                                                    <span className="info-icon" title="Number of refunds currently pending (Created + Pending states)">
                                    ℹ️
                                  </span>
                                                                </h3>
                                                            </div>
                                                            <div className="flow-card-content">
                                                                <div className="count-center">
                                                                    <div className="refund-count"><NumberFormatter number={trackerData?.refundsPending || 0} /></div>
                                                                </div>
                                                                <div className="card-actions">
                                                                    <KatButton
                                                                        variant="primary"
                                                                        label="Analyze"
                                                                        onClick={() => {
                                                                            if (showPendingAnalysis) setShowOverSlaAnalysis(false);
                                                                            setShowPendingAnalysis(!showPendingAnalysis);
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className="approved-declined-container">
                                                            <div id="approved-card" className="flow-card approved-card">
                                                                <div className="card-header">
                                                                    <h3>
                                    <span className="title-left">
                                      <IconApproved className="card-icon" />
                                      Refunds Approved
                                    </span>
                                                                        <span className="info-icon" title="Number of refunds that have been approved">
                                      ℹ️
                                    </span>
                                                                    </h3>
                                                                </div>
                                                                <div className="flow-card-content">
                                                                    <div className="count-center">
                                                                        <div className="refund-count"><NumberFormatter number={trackerData?.approved || 0} /></div>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            <div id="declined-card" className="flow-card declined-card">
                                                                <div className="card-header">
                                                                    <h3>
                                    <span className="title-left">
                                      <IconDeclined className="card-icon" />
                                      Refunds Declined
                                    </span>
                                                                        <span className="info-icon" title="Number of refunds that have been declined">
                                      ℹ️
                                    </span>
                                                                    </h3>
                                                                </div>
                                                                <div className="flow-card-content">
                                                                    <div className="count-center">
                                                                        <div className="refund-count"><NumberFormatter number={trackerData?.declined || 0} /></div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Level 1 arrows */}
                                                    {trackerData && selectedTrackerTab === 'tracker' && (
                                                        <>
                                                            <ArrowComponent id="level1-total-to-pending" from="total-created-card" to="pending-card" />
                                                            <ArrowComponent id="level1-pending-to-approved" from="pending-card" to="approved-card" />
                                                            <ArrowComponent id="level1-pending-to-declined" from="pending-card" to="declined-card" />
                                                        </>
                                                    )}

                                                    {/* Level 2 */}
                                                    {showPendingAnalysis && (
                                                        <div className="level-2-analysis">
                                                            <div className="sla-analysis-container">
                                                                <div id="within-sla-card" className="flow-card within-sla-card">
                                                                    <div className="card-header">
                                                                        <h3>
                                      <span className="title-left">
                                        <IconWithinSla className="card-icon" />
                                        Within SLA
                                      </span>
                                                                            <span className="info-icon" title="Number of pending refunds within SLA timeframe">
                                        ℹ️
                                      </span>
                                                                        </h3>
                                                                    </div>
                                                                    <div className="flow-card-content">
                                                                        <div className="count-center">
                                                                            <div className="refund-count"><NumberFormatter number={trackerData?.withinSla || 0} /></div>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <div id="over-sla-card" className="flow-card over-sla-card">
                                                                    <div className="card-header">
                                                                        <h3>
                                      <span className="title-left">
                                        <IconOverSla className="card-icon" />
                                        Over SLA
                                      </span>
                                                                            <span className="info-icon" title="Number of pending refunds over SLA timeframe">
                                        ℹ️
                                      </span>
                                                                        </h3>
                                                                    </div>
                                                                    <div className="flow-card-content">
                                                                        <div className="count-center">
                                                                            <div className="refund-count"><NumberFormatter number={trackerData?.overSla || 0} /></div>
                                                                        </div>
                                                                        <div className="card-actions">
                                                                            <KatButton
                                                                                variant="primary"
                                                                                label="Analyze"
                                                                                onClick={() => setShowOverSlaAnalysis(!showOverSlaAnalysis)}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Level 2 arrows */}
                                                    {showPendingAnalysis && trackerData && selectedTrackerTab === 'tracker' && (
                                                        <>
                                                            <ArrowComponent
                                                                id="level2-pending-to-within-sla"
                                                                from="pending-card"
                                                                to="within-sla-card"
                                                                startSocket="bottom"
                                                                endSocket="top"
                                                            />
                                                            <ArrowComponent
                                                                id="level2-pending-to-over-sla"
                                                                from="pending-card"
                                                                to="over-sla-card"
                                                                startSocket="bottom"
                                                                endSocket="top"
                                                            />
                                                        </>
                                                    )}

                                                    {/* Level 3 */}
                                                    {showPendingAnalysis && showOverSlaAnalysis && (
                                                        <div className="level-3-breakdown">
                                                            <div id="breakdown-container" className="over-sla-breakdown-container">
                                                                <div className="breakdown-cards">
                                                                    <div id="processor-card" className="flow-card processor-card">
                                                                        <div className="card-header">
                                                                            <h3>
                                                                                <span className="title-left">Pending with Processor</span>
                                                                                <span className="info-icon" title="Refunds that have been batched and sent to processor">
                                          ℹ️
                                        </span>
                                                                            </h3>
                                                                        </div>
                                                                        <div className="flow-card-content">
                                                                            <div className="count-center">
                                                                                <div className="refund-count"><NumberFormatter number={trackerData?.batched || 0} /></div>
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    <div id="platform-card" className="flow-card platform-card">
                                                                        <div className="card-header">
                                                                            <h3>
                                                                                <span className="title-left">Pending on Platform</span>
                                                                                <span className="info-icon" title="Refunds that are unbatched and pending on platform">
                                          ℹ️
                                        </span>
                                                                            </h3>
                                                                        </div>
                                                                        <div className="flow-card-content">
                                                                            <div className="count-center">
                                                                                <div className="refund-count"><NumberFormatter number={trackerData?.unbatched || 0} /></div>
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    <div id="unstarted-card" className="flow-card unstarted-card">
                                                                        <div className="card-header">
                                                                            <h3>
                                                                                <span className="title-left">Not Started</span>
                                                                                <span className="info-icon" title="Refunds that have not started processing">
                                          ℹ️
                                        </span>
                                                                            </h3>
                                                                        </div>
                                                                        <div className="flow-card-content">
                                                                            <div className="count-center">
                                                                                <div className="refund-count"><NumberFormatter number={trackerData?.unstarted || 0} /></div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <div className="breakdown-actions">
                                                                    <KatButton
                                                                        variant="primary"
                                                                        label={isDiagnosticSyncing ? 'Syncing Filters...' : 'Get Diagnostics'}
                                                                        onClick={handleGetDiagnostics}
                                                                        disabled={isDiagnosticSyncing}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Level 3 arrow */}
                                                    {showPendingAnalysis && showOverSlaAnalysis && trackerData && selectedTrackerTab === 'tracker' && (
                                                        <ArrowComponent id="level3-over-sla-to-breakdown" from="over-sla-card" to="breakdown-container" startSocket="bottom" endSocket="top" />
                                                    )}
                                                </div>
                                            </Xwrapper>
                                        )}
                                    </div>

                                    <div className="trend-date-range" style={{ marginBottom: '12px' }}>
                                        {trendStartDateTime && trendEndDateTime && (
                                            <TrendRangeSlider
                                                windowStart={trendWindow.startDate}
                                                windowEnd={trendWindow.endDate}
                                                selectedStart={trendStartDateTime}
                                                selectedEnd={trendEndDateTime}
                                                windowLabel={TIME_WINDOW_OPTIONS.find(
                                                    (o) => o.value === filters.timeWindow)?.label}
                                                onChange={(start, end) => {
                                                    setTrendStartDateTime(start);
                                                    setTrendEndDateTime(end);
                                                }}
                                            />
                                        )}
                                    </div>

                                    <TrackerTrendPanel
                                        stage={selectedEnvironment.stage}
                                        realm={selectedEnvironment.realm}
                                        trackerData={trackerData}
                                        startDateTime={trendStartDateTime}
                                        endDateTime={trendEndDateTime}
                                    />

                                    <BacklogBurndownPanel
                                        stage={selectedEnvironment.stage}
                                        realm={selectedEnvironment.realm}
                                        trackerData={trackerData}
                                        startDateTime={trendStartDateTime}
                                        endDateTime={trendEndDateTime}
                                    />

                                    <PendingDeltaPanel
                                        stage={selectedEnvironment.stage}
                                        realm={selectedEnvironment.realm}
                                        trackerData={trackerData}
                                        startDateTime={trendStartDateTime}
                                        endDateTime={trendEndDateTime}
                                    />

                                </div>
                            </KatTab>

                            <KatTab tabId="diagnostic" label="Diagnostic View" onClick={() => handleTabSwitch('diagnostic')}>
                                <div className="tab-content">
                                    <DiagnosticView
                                        stage={selectedEnvironment.stage}
                                        realm={selectedEnvironment.realm}
                                        triggerFetch={triggerDiagnosticFetch}
                                        filtersFromTracker={diagnosticFiltersFromTracker}
                                        trackerGeneratedAt={trackerData?.metadata?.generatedAt || null}
                                        onFiltersApplied={() => {
                                            setDiagnosticFiltersFromTracker(null);
                                            setIsDiagnosticSyncing(false);
                                        }}
                                    />
                                </div>
                            </KatTab>
                        </KatTabs>
                    </div>
                </>
            )}
            
        </div>
    );
};

export default SherlockDashboard;
